import { useState, type ReactNode } from "react";
import { AlertTriangle, CheckCircle2, EyeOff, RotateCcw, Trash2 } from "lucide-react";
import type { ModerationAction, ModerationQueueItem } from "@shared/types/communityAdmin";
import { Button } from "../../../components/ui/Button";
import { Card } from "../../../components/ui/Card";
import { EmptyState } from "../../../components/ui/EmptyState";
import { PageLoader } from "../../../components/ui/Spinner";
import { cn } from "../../../lib/cn";
import { FOCUS_RING } from "../../../lib/focusRing";
import { useToast } from "../../../lib/toast/ToastProvider";
import { REPORT_REASON_LABELS } from "../contract";
import { SampleChip } from "../components/SampleChip";
import { useModerationDecision, useModerationQueue, type ModerationView } from "./api";

const SOURCE_LABEL: Record<ModerationQueueItem["source"], string> = {
  keyword: "Held by the keyword screen",
  report: "Reported by members",
  safety: "Safety check-in (posted)",
};

const STATUS_LABEL: Record<ModerationQueueItem["contentStatus"], string> = {
  visible: "Visible to everyone",
  held: "Held — author only",
  hidden: "Hidden after reports — author only",
  removed: "Removed",
};

/**
 * Platform console → Moderation. Held and reported posts and replies, urgent
 * first: approve (make visible), remove (gone for everyone) or restore a
 * removed one. Moderators see the real author of anonymous posts here — and
 * only here. Posts, replies and account emails only; never a child record.
 */
export function ModerationPanel() {
  const [view, setView] = useState<ModerationView>("pending");
  const { data, isLoading, error } = useModerationQueue(view);
  const decide = useModerationDecision();
  const { publish } = useToast();

  const act = (item: ModerationQueueItem, action: ModerationAction) =>
    decide.mutate(
      { itemId: item.id, action },
      {
        onSuccess: () =>
          publish({
            variant: "success",
            title:
              action === "approve" ? "Approved — it's visible" : action === "remove" ? "Removed" : "Restored",
            duration: 2500,
          }),
        onError: (err) => publish({ variant: "error", title: "Couldn't save that decision", description: err.message }),
      },
    );

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Which items">
        <ViewPill active={view === "pending"} onClick={() => setView("pending")}>
          Waiting{data && view === "pending" ? ` (${data.pendingCount})` : ""}
        </ViewPill>
        <ViewPill active={view === "decided"} onClick={() => setView("decided")}>
          Recently decided
        </ViewPill>
      </div>

      <div className="mt-4">
        {isLoading ? (
          <PageLoader label="Loading the queue…" />
        ) : error ? (
          <Card className="p-6 text-sm text-red-700">Couldn&apos;t load the moderation queue.</Card>
        ) : !data || data.items.length === 0 ? (
          <EmptyState
            title={view === "pending" ? "Nothing waiting" : "No decisions yet"}
            description={
              view === "pending"
                ? "Held and reported posts and replies show up here, urgent ones first."
                : "Approved and removed items show up here."
            }
          />
        ) : (
          <ul className="space-y-3">
            {data.items.map((item) => (
              <li key={item.id}>
                <QueueCard item={item} busy={decide.isPending} onAct={(a) => act(item, a)} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function ViewPill({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        "rounded-full px-3.5 py-1.5 text-sm font-medium transition",
        FOCUS_RING,
        active
          ? "bg-gradient-to-br from-sky-500 to-indigo-600 text-white shadow-soft"
          : "bg-white/80 text-neutral-700 ring-1 ring-white hover:bg-white hover:text-sky-700",
      )}
    >
      {children}
    </button>
  );
}

function QueueCard({
  item,
  busy,
  onAct,
}: {
  item: ModerationQueueItem;
  busy: boolean;
  onAct: (action: ModerationAction) => void;
}) {
  return (
    <Card className="p-4 sm:p-5">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        {item.priority === "urgent" && (
          <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 font-semibold text-rose-800">
            <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
            Urgent
          </span>
        )}
        <span className="rounded-full bg-neutral-100 px-2 py-0.5 font-medium text-neutral-800">
          {item.targetType === "post" ? "Post" : "Reply"}
        </span>
        <span className="font-medium text-neutral-700">{SOURCE_LABEL[item.source]}</span>
        {item.reportCount > 0 && (
          <span className="rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-800">
            {item.reportCount} {item.reportCount === 1 ? "report" : "reports"}
          </span>
        )}
        {item.isSample && <SampleChip />}
        <span className="ml-auto text-neutral-500">{new Date(item.createdAt).toLocaleString()}</span>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
        <span className="font-semibold text-neutral-900">{item.author.displayName}</span>
        <span className="text-neutral-500">{item.author.email}</span>
        {item.anonymous && (
          <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-800">
            <EyeOff className="h-3 w-3" aria-hidden />
            Posted anonymously — only moderators see this name
          </span>
        )}
      </div>

      <p className="mt-2 line-clamp-6 whitespace-pre-line text-sm leading-relaxed text-neutral-800">{item.text}</p>

      {(item.reasons.length > 0 || item.reportReasons.length > 0) && (
        <ul className="mt-3 flex flex-wrap gap-1.5 text-xs">
          {item.reasons.map((r) => (
            <li key={`${r.code}:${r.label}`} className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-amber-800">
              {r.label}: <span className="font-semibold">&ldquo;{r.matched}&rdquo;</span>
            </li>
          ))}
          {item.reportReasons.map((r) => (
            <li key={r} className="rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-rose-800">
              {REPORT_REASON_LABELS[r].label}
            </li>
          ))}
        </ul>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-sky-100/80 pt-3">
        <span className="text-xs text-neutral-600">{STATUS_LABEL[item.contentStatus]}</span>
        {item.decisionNote && <span className="text-xs text-neutral-500">· “{item.decisionNote}”</span>}
        <div className="ml-auto flex flex-wrap gap-2">
          {item.status === "pending" && (
            <Button size="sm" variant="secondary" disabled={busy} onClick={() => onAct("approve")}>
              <CheckCircle2 className="h-4 w-4" aria-hidden />
              Approve
            </Button>
          )}
          {item.status !== "removed" && (
            <Button size="sm" variant="destructiveOutline" disabled={busy} onClick={() => onAct("remove")}>
              <Trash2 className="h-4 w-4" aria-hidden />
              Remove
            </Button>
          )}
          {item.status === "removed" && (
            <Button size="sm" variant="secondary" disabled={busy} onClick={() => onAct("restore")}>
              <RotateCcw className="h-4 w-4" aria-hidden />
              Restore
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}
